// ═══════════════════════════════════════════════════════════════════════════
// PageProgressBar — thin top-of-viewport progress bar that shows when the
// app navigates between views. Mirrors the NProgress / YouTube pattern:
// fires whenever `view` changes, animates 0 → 80% fast, holds, then
// completes to 100% and fades out.
//
// Transparent backdrop so it sits over the new #0d0e0f app background
// without painting a stripe — only the moving bar itself is visible.
// The bar color comes from --link (brand red).
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';

export default function PageProgressBar({ view }) {
  const [phase, setPhase] = useState('idle'); // idle | start | hold | done
  const timers = useRef([]);
  const prevView = useRef(view);

  useEffect(() => {
    if (prevView.current === view) return;
    prevView.current = view;

    // Clear any pending animation steps from the previous nav so back-
    // to-back changes don't pile up.
    timers.current.forEach(clearTimeout);
    timers.current = [];

    setPhase('start');
    timers.current.push(setTimeout(() => setPhase('hold'), 30));
    timers.current.push(setTimeout(() => setPhase('done'), 320));
    timers.current.push(setTimeout(() => setPhase('idle'), 560));

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [view]);

  if (phase === 'idle') return null;
  return (
    <div className="page-progress" aria-hidden>
      <div className={`page-progress-bar page-progress-bar--${phase}`} />
    </div>
  );
}
